import { useCallback, useMemo, useState } from "react";
import type { FormEvent } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Close, Search } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, Button, Checkbox, DotsLoader, IconButton, Modal } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { IUser } from "app/@types/ISwSettings";
import { swSettings } from "app/common/services/swSettings";

import type { TeamUser } from "../../types/teams.types";
import { StyledRow } from "./styles";

const LIST_HEIGHT = "280px";

type TeamMembersStepProps = {
    teamName: string;
    users: TeamUser[];
    isUsersLoading: boolean;
    isUsersError: boolean;
    selectedMembers: TeamUser[];
    onSelectionChange: (members: TeamUser[]) => void;
    onBack: () => void;
    onCancel: () => void;
    onSubmit: () => void;
    isSubmitting: boolean;
    submitLabelKey: string;
    hideBack?: boolean;
};

export const TeamMembersStep = ({
    teamName,
    users,
    isUsersLoading,
    isUsersError,
    selectedMembers,
    onSelectionChange,
    onBack,
    onCancel,
    onSubmit,
    isSubmitting,
    submitLabelKey,
    hideBack = false,
}: TeamMembersStepProps) => {
    const { translate } = useTranslate();
    const [query, setQuery] = useState("");

    const currentUserId = (swSettings.user as Pick<IUser, "id"> | undefined)?.id;

    const selectedIdSet = useMemo(() => new Set(selectedMembers.map((m) => m.userId)), [selectedMembers]);

    const filteredUsers = useMemo(() => {
        const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (terms.length === 0) {
            return users;
        }

        const matchesUser = (user: TeamUser): boolean => {
            const name = user.name?.toLowerCase() ?? "";
            const email = user.email?.toLowerCase() ?? "";
            return terms.every((term) => name.includes(term) || email.includes(term));
        };

        const startsWith: TeamUser[] = [];
        const contains: TeamUser[] = [];

        users.forEach((user) => {
            if (!matchesUser(user)) {
                return;
            }

            const name = user.name?.toLowerCase() ?? "";
            const email = user.email?.toLowerCase() ?? "";
            const allPrefix = terms.every((term) => name.startsWith(term) || email.startsWith(term));

            if (allPrefix) {
                startsWith.push(user);
                return;
            }

            contains.push(user);
        });

        return [...startsWith, ...contains];
    }, [users, query]);

    const showSelectAll = query.trim().length === 0 && users.length > 1;
    const isAllSelected = users.length > 0 && users.every((u) => selectedIdSet.has(u.userId));
    const isPartiallySelected = !isAllSelected && users.some((u) => selectedIdSet.has(u.userId));

    const handleToggleUser = useCallback(
        (user: TeamUser) => {
            if (selectedIdSet.has(user.userId)) {
                onSelectionChange(selectedMembers.filter((m) => m.userId !== user.userId));
                return;
            }
            onSelectionChange([...selectedMembers, user]);
        },
        [selectedIdSet, selectedMembers, onSelectionChange],
    );

    const handleToggleAll = useCallback(() => {
        if (isAllSelected) {
            const usersIdSet = new Set(users.map((u) => u.userId));
            onSelectionChange(selectedMembers.filter((m) => !usersIdSet.has(m.userId)));
            return;
        }
        const additions = users.filter((u) => !selectedIdSet.has(u.userId));
        onSelectionChange([...selectedMembers, ...additions]);
    }, [isAllSelected, users, selectedMembers, selectedIdSet, onSelectionChange]);

    const isSubmitDisabled = selectedMembers.length === 0 || isSubmitting;

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (isSubmitDisabled) {
            return;
        }
        onSubmit();
    };

    return (
        <Box
            as="form"
            onSubmit={handleSubmit}
            noValidate
            data-automation="team-members-form"
        >
            <Modal.Header withBorder>
                <Flex
                    gap="8px"
                    alignItems="flex-start"
                >
                    {hideBack ? null : (
                        <IconButton
                            type="flat"
                            buttonHtmlType="button"
                            iconSize="sm"
                            iconName="arrow-left"
                            onClick={onBack}
                            isDisabled={isSubmitting}
                            dataAutomation="team-members-back"
                        />
                    )}
                    <Stack gap="8px">
                        <Modal.Title data-automation="team-members-modal-title">
                            {translate("si.management.teams.create.members.title")}
                        </Modal.Title>
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon["300"]}
                            data-automation="team-members-modal-subtitle"
                        >
                            {translate("si.management.teams.create.members.subtitle", { name: teamName })}
                        </Text>
                    </Stack>
                </Flex>
            </Modal.Header>

            <Modal.Content>
                <Stack
                    gap="12px"
                    padding="16px 0px"
                >
                    <Text
                        fontVariant="body1"
                        color={colorsPalettes.carbon["400"]}
                        as="span"
                    >
                        {translate("si.management.teams.create.members.search.label")}
                    </Text>

                    <TextField
                        autoFocus
                        value={query}
                        placeholder={translate("si.management.teams.create.members.search.placeholder")}
                        startAddon={<Search size="sm" />}
                        endAddon={
                            isUsersLoading ? (
                                <DotsLoader />
                            ) : query.length > 0 ? (
                                <Close
                                    size="sm"
                                    cursor="pointer"
                                    onClick={() => setQuery("")}
                                />
                            ) : null
                        }
                        type="text"
                        onChange={setQuery}
                        isDisabled={isSubmitting}
                    />

                    <Stack
                        data-automation="team-members-list"
                        maxHeight={LIST_HEIGHT}
                        minHeight={LIST_HEIGHT}
                        overflowY="auto"
                        border={`1px solid ${colorsPalettes.carbon["100"]}`}
                        borderRadius="4px"
                    >
                        {isUsersLoading ? (
                            <Flex
                                justifyContent="center"
                                padding="16px"
                            >
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon["300"]}
                                >
                                    {translate("si.management.teams.create.members.search.loading")}
                                </Text>
                            </Flex>
                        ) : isUsersError ? (
                            <Flex
                                justifyContent="center"
                                padding="16px"
                            >
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon["300"]}
                                >
                                    {translate("si.management.teams.create.members.search.error")}
                                </Text>
                            </Flex>
                        ) : filteredUsers.length === 0 ? (
                            <Flex
                                justifyContent="center"
                                padding="16px"
                            >
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon["300"]}
                                >
                                    {translate("si.management.teams.create.members.search.empty")}
                                </Text>
                            </Flex>
                        ) : (
                            <>
                                {showSelectAll ? (
                                    <StyledRow
                                        type="button"
                                        onClick={handleToggleAll}
                                        aria-pressed={isAllSelected}
                                        data-automation="team-members-row-select-all"
                                    >
                                        <Text
                                            fontVariant="body2"
                                            color={colorsPalettes.carbon["500"]}
                                            as="span"
                                        >
                                            {`${translate("pro_logic_components.table.selection.dropdown.select_all")} ${users.length ? `(${users.length})` : ""}`}
                                        </Text>
                                        <Checkbox
                                            selected={isAllSelected}
                                            halfSelected={isPartiallySelected}
                                        />
                                    </StyledRow>
                                ) : null}
                                {filteredUsers.map((user) => (
                                    <StyledRow
                                        key={user.userId}
                                        type="button"
                                        onClick={() => handleToggleUser(user)}
                                        aria-pressed={selectedIdSet.has(user.userId)}
                                        data-automation={`team-members-row-${user.userId}`}
                                    >
                                        <Stack
                                            minWidth={0}
                                            flex="1"
                                        >
                                            <Flex
                                                alignItems="center"
                                                gap="8px"
                                            >
                                                <Text
                                                    fontVariant="body2"
                                                    color={colorsPalettes.carbon["500"]}
                                                    as="span"
                                                >
                                                    {user.name ?? user.email ?? ""}
                                                </Text>
                                                {currentUserId !== undefined && user.userId === currentUserId ? (
                                                    <Badge
                                                        bgColor={colorsPalettes.midnight["300"]}
                                                        color={colorsPalettes.carbon["0"]}
                                                        type="custom"
                                                    >
                                                        {translate("si.management.teams.detail.badge.me")}
                                                    </Badge>
                                                ) : null}
                                            </Flex>
                                            {user.name && user.email ? (
                                                <Text
                                                    fontVariant="caption"
                                                    color={colorsPalettes.carbon["300"]}
                                                    as="span"
                                                >
                                                    {user.email}
                                                </Text>
                                            ) : null}
                                        </Stack>
                                        <Checkbox selected={selectedIdSet.has(user.userId)} />
                                    </StyledRow>
                                ))}
                            </>
                        )}
                    </Stack>
                </Stack>
            </Modal.Content>

            <Modal.Footer>
                <Flex
                    alignItems="center"
                    gap="8px"
                    justifyContent="space-between"
                    flex={1}
                    padding="0px 0px 0px 8px"
                >
                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.carbon["300"]}
                    >
                        {translate("si.management.teams.detail.members_count", {
                            count: selectedMembers.length,
                        })}
                    </Text>
                    <Flex gap="8px">
                        <Button
                            type="flat"
                            buttonHtmlType="button"
                            onClick={onCancel}
                            isDisabled={isSubmitting}
                            dataAutomation="team-members-cancel"
                        >
                            {translate("si.common.button.cancel")}
                        </Button>
                        <Button
                            type="primary"
                            buttonHtmlType="submit"
                            isDisabled={isSubmitDisabled}
                            isLoading={isSubmitting}
                            dataAutomation="team-members-create"
                        >
                            {translate(submitLabelKey)}
                        </Button>
                    </Flex>
                </Flex>
            </Modal.Footer>
        </Box>
    );
};
